import { useEffect } from "react";

/**
 * usePageMeta — set document.title, meta description, canonical, Open Graph
 * / Twitter card tags AND inject JSON-LD structured data so Google can show
 * rich results (stars, price, availability) in the SERP.
 */
export default function usePageMeta({
  title,
  description,
  image,
  url,
  type = "website",
  jsonLd,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    const upsertLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");

    upsertMeta("name", "description", description);
    upsertLink("canonical", finalUrl);

    // Open Graph
    upsertMeta("property", "og:site_name", "Vape Essence");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", finalUrl);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:locale", "en_ZA");

    // Twitter
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:site", "@vapeessence");
    upsertMeta("name", "twitter:creator", "@vapeessence");

    // JSON-LD structured data (can be one object or an array)
    const existing = document.head.querySelector('script[data-testid="page-jsonld"]');
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-testid", "page-jsonld");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, image, url, type, JSON.stringify(jsonLd || null)]);
}

/* ----- Schema builders (pure functions) ----- */

const SITE = "https://vapeessence.co.za";
const CURRENCY = "ZAR";

export function buildProductSchema({ product, reviewSummary, url }) {
  if (!product) return null;
  const inStock = (product.stock ?? 0) > 0;
  const images = (product.images || []).map((img) =>
    typeof img === "string" && img.startsWith("http") ? img : `${SITE}${img}`
  );
  const productUrl = url || `${SITE}/product/${product.slug}`;

  const offers = {
    "@type": "Offer",
    url: productUrl,
    priceCurrency: CURRENCY,
    price: Number(product.price).toFixed(2),
    availability: inStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: { "@type": "Organization", name: "Vape Essence" },
  };
  if (product.compare_at && product.compare_at > product.price) {
    offers.priceValidUntil = new Date(Date.now() + 90 * 864e5)
      .toISOString()
      .slice(0, 10);
  }

  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    image: images.length ? images : undefined,
    description: product.description,
    sku: product.slug,
    brand: { "@type": "Brand", name: product.brand || "Vape Essence" },
    category: product.category,
    offers,
  };

  if (reviewSummary?.count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(reviewSummary.average).toFixed(1),
      reviewCount: reviewSummary.count,
      bestRating: "5",
      worstRating: "1",
    };
  }
  return schema;
}

export function buildOrganizationSchema({ content } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    name: "Vape Essence",
    url: SITE,
    logo: `${SITE}/logo512.png`,
    description:
      "Premium disposable vapes and nicotine pouches. Curated, authentic, delivered nationwide in South Africa.",
    address: content?.contact_address
      ? {
          "@type": "PostalAddress",
          streetAddress: content.contact_address,
          addressCountry: "ZA",
        }
      : undefined,
    contactPoint: content?.contact_email
      ? [
          {
            "@type": "ContactPoint",
            contactType: "customer service",
            email: content.contact_email,
            telephone: content.contact_phone,
            areaServed: "ZA",
            availableLanguage: ["English"],
          },
        ]
      : undefined,
    sameAs: [
      "https://www.tiktok.com/@vapeessence",
      "https://www.instagram.com/vapeessence",
      "https://www.facebook.com/vapeessence",
    ],
  };
}

export function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Vape Essence",
    url: SITE,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE}/shop?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE}${it.url}`,
    })),
  };
}

export function buildArticleSchema({ post, content }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image || undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Organization", name: "Vape Essence" },
    publisher: {
      "@type": "Organization",
      name: "Vape Essence",
      logo: { "@type": "ImageObject", url: `${SITE}/logo512.png` },
    },
    mainEntityOfPage: `${SITE}/journal/${post.slug}`,
  };
}
